import CCText from '@/Atoms/CCText';
import CCTextInput from '@/Atoms/CCTextInput';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {useEffect, useState} from 'react';
import {FlatList, TouchableOpacity, View, Image} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import {styles} from '../style';
import {Video} from 'expo-av';
import CCButton from '@/Atoms/CCButton';
import SupportModal from '@/Components/UserAssistance/component/SupportModal';
import {formatDate, formatDateSeparator, isSameDay} from '@/Utils/CommonUtils';
import Box from '@/Atoms/Box';
import logoIcon from '../../../Assets/Images/Svgs/logo.svg';
import chatLogo from '../../../Assets/Images/Svgs/chatLogo.svg';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import {RAISE_A_TICKET} from '@/Utils/Constants';
import {LinearGradient} from 'expo-linear-gradient';

export type Message = {
  id: string;
  text: string;
  from: 'left' | 'right';
  user: string;
};

export interface ChatViewProps {
  ticket?: {};
  details: {
    id?: number | string;
    title?: string;
    comments?: any[];
    name?: string;
  };
  file: File | null;
  setFile: React.Dispatch<React.SetStateAction<File | null>>;
  message: string;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  handleSend: () => void;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  flatListRef: React.RefObject<FlatList>;
  messages: Message[];
  isTicketList?: boolean;
  showTime?: boolean;
  refresh?: boolean;
  handleRefresh?: () => void;
}

const ChatView: React.FC<ChatViewProps> = ({
  ticket,
  details,
  file,
  setFile,
  message,
  setMessage,
  handleSend,
  handleFileChange,
  flatListRef,
  messages = [],
  isTicketList = true,
  showTime = false,
  refresh = false,
  handleRefresh,
}) => {
  if (!details) return null;
  const [openModal, setOpenModal] = useState(false);

  // Function to check if we should show date separator
  const shouldShowDateSeparator = (currentItem: any, previousItem: any) => {
    if (!previousItem) return true;
    return !isSameDay(currentItem.created_at, previousItem.created_at);
  };

  useEffect(() => {
    if (flatListRef?.current && messages?.length > 0) {
      setTimeout(() => {
        if (isTicketList) {
          flatListRef.current?.scrollToEnd({animated: true});
        } else {
          flatListRef.current?.scrollToOffset({offset: 0, animated: true});
        }
      }, 300);
    }
  }, [messages, ticket]);

  return (
    <View style={styles.detailsContainer}>
      {/* Header */}
      <View
        style={[
          styles.rightPanelHeader,
          {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
        ]}>
        <Box
          style={{
            flexDirection: 'row',
            alignItems: 'center',
          }}>
          {!isTicketList && (
            <View
              style={[
                styles.avatar,
                {
                  height: 30,
                  width: 30,
                },
              ]}>
              <Image
                source={logoIcon}
                style={{
                  height: 25,
                  width: 25,
                }}
              />
            </View>
          )}
          <CCText fontSize={FontSizes[16]} style={styles.detailsText}>
            {isTicketList
              ? ` # ${details.id} | ${details.title}`
              : ` ${details.name}`}
          </CCText>
        </Box>
        {refresh && (
          <Box style={styles.refresh} onPress={handleRefresh}>
            <MaterialCommunityIcons
              color={CCColors.Primary.Brown}
              name="refresh"
              size={20}
            />
          </Box>
        )}
      </View>

      {/* Chat body */}
      <FlatList
        inverted={!isTicketList}
        ref={flatListRef}
        data={messages}
        keyExtractor={item => item?.id}
        renderItem={({item, index}) => {
          const compareItem = isTicketList
            ? index > 0
              ? messages[index - 1]
              : null
            : index < messages.length - 1
            ? messages[index + 1]
            : null;

          const showDateSeparator = shouldShowDateSeparator(item, compareItem);

          return (
            <>
              {showDateSeparator && isTicketList && (
                <View style={styles.dateSeparator}>
                  <CCText
                    fontWeight={600}
                    fontSize={FontSizes[12]}
                    color={CCColors.TextColor.GreyText}
                    style={styles.dateSeparatorText}>
                    {formatDateSeparator(item?.created_at)}
                  </CCText>
                </View>
              )}
              <View
                style={[
                  styles.messageRow,
                  item?.from === 'left' ? styles.leftRow : styles.rightRow,
                ]}>
                {item?.from === 'left' && (
                  <View
                    style={[
                      styles.avatar,
                      {
                        marginTop: isTicketList ? 0 : 8,
                        height: isTicketList ? 40 : 30,
                        width: isTicketList ? 40 : 30,
                      },
                    ]}>
                    {!isTicketList ? (
                      <Image
                        source={logoIcon}
                        style={{
                          height: 25,
                          width: 25,
                        }}
                      />
                    ) : (
                      <Image
                        source={chatLogo}
                        style={{
                          height: 24,
                          width: 24,
                        }}
                      />
                    )}
                  </View>
                )}
                <Box
                  flexDirection="column"
                  style={{
                    maxWidth: '75%',
                  }}
                  alignItems={
                    item?.from === 'left' ? 'flex-start' : 'flex-end'
                  }>
                  {item?.comment_type == 4 &&
                  item?.from === 'left' &&
                  isTicketList ? (
                    <LinearGradient
                      colors={[CCColors.Primary.Default, CCColors.Red]}
                      start={{x: 0, y: 0}}
                      end={{x: 1, y: 0}}
                      style={{
                        padding: 1, // this becomes the border thickness
                        borderRadius: 20,
                      }}>
                      {/* INNER gradient (background) */}
                      <LinearGradient
                        colors={[
                          CCColors.MeetingStatus.LightYellow,
                          CCColors.MeetingStatus.LightRed,
                        ]}
                        start={{x: 0, y: 0}}
                        end={{x: 1, y: 0}}
                        style={[
                          styles.chatBubbleLeft,
                          {
                            paddingVertical: 10,
                            borderRadius: 20,
                            borderWidth: 0,
                          },
                        ]}>
                        <CCText
                          fontSize={
                            isTicketList ? FontSizes[16] : FontSizes[14]
                          }>
                          {item?.text}
                        </CCText>

                        {/* Render attachment if any */}
                        {item?.uploaded_file &&
                          (item?.uploaded_file.endsWith('.mp4') ||
                          item?.uploaded_file.includes('video') ? (
                            <Video
                              source={{uri: item?.uploaded_file}}
                              style={{width: 200, height: 200, marginTop: 8}}
                              resizeMode="contain"
                              shouldPlay={false}
                              isLooping
                            />
                          ) : (
                            <Image
                              source={{uri: item?.uploaded_file}}
                              style={{width: 200, height: 200, marginTop: 8}}
                              resizeMode="contain"
                            />
                          ))}
                      </LinearGradient>
                    </LinearGradient>
                  ) : (
                    <View
                      style={
                        item?.from === 'left'
                          ? [
                              styles.chatBubbleLeft,
                              {
                                paddingVertical: 10,
                              },
                            ]
                          : [
                              styles.chatBubbleRight,
                              {
                                paddingVertical: 10,
                              },
                            ]
                      }>
                      <CCText
                        fontSize={isTicketList ? FontSizes[16] : FontSizes[14]}>
                        {item?.text}
                      </CCText>

                      {/* Render attachment if any */}
                      {item?.uploaded_file &&
                        (item?.uploaded_file.endsWith('.mp4') ||
                        item?.uploaded_file.includes('video') ? (
                          <Video
                            source={{uri: item?.uploaded_file}}
                            style={{width: 200, height: 200, marginTop: 8}}
                            resizeMode="contain"
                            shouldPlay={false}
                            isLooping
                          />
                        ) : (
                          <Image
                            source={{uri: item?.uploaded_file}}
                            style={{width: 200, height: 200, marginTop: 8}}
                            resizeMode="contain"
                          />
                        ))}
                    </View>
                  )}

                  {isTicketList && item?.from === 'left' ? (
                    <CCText
                      style={{
                        margin: 4,
                      }}
                      color={CCColors.TextColor.GreyText}
                      fontSize={FontSizes[10]}>
                      {formatDate(item?.created_at).split('@')[1]}
                      {item?.comment_type === 4
                        ? ` | By System`
                        : ` | By Agent`}
                    </CCText>
                  ) : (
                    <CCText
                      style={{
                        margin: 4,
                      }}
                      color={CCColors.TextColor.GreyText}
                      fontSize={FontSizes[10]}>
                      {formatDate(item?.created_at).split('@')[1]}
                    </CCText>
                  )}
                </Box>
                {item?.from === 'right' && (
                  <View style={styles.avatar}>
                    <CCText fontSize={FontSizes[10]} style={styles.avatarText}>
                      {item?.user}
                    </CCText>
                  </View>
                )}
              </View>
              {showDateSeparator && !isTicketList && (
                <View style={styles.dateSeparator}>
                  <CCText
                    fontWeight={600}
                    fontSize={FontSizes[12]}
                    color={CCColors.TextColor.GreyText}
                    style={styles.dateSeparatorText}>
                    {formatDateSeparator(item?.created_at)}
                  </CCText>
                </View>
              )}
            </>
          );
        }}
        contentContainerStyle={[
          styles.chatContent,
          {
            justifyContent: isTicketList ? 'flex-end' : 'flex-start',
            flexGrow: 1,
          },
        ]}
        showsVerticalScrollIndicator={false}
        style={styles.chatBody}
      />

      {/* File preview (if attached) */}
      {file && (
        <View style={styles.filePreviewContainer}>
          {file.type.startsWith('image/') ? (
            <Image
              source={{uri: URL.createObjectURL(file)}}
              style={styles.filePreviewImage}
              resizeMode="cover"
            />
          ) : file.type.startsWith('video/') ? (
            <Video
              source={{uri: URL.createObjectURL(file)}}
              style={styles.filePreviewVideo}
              resizeMode="cover"
              muted
            />
          ) : (
            <CCText style={styles.fileName}>{file.name}</CCText>
          )}

          <TouchableOpacity
            onPress={() => setFile && setFile(null)}
            style={styles.removeFileBtn}>
            <MaterialIcons name="close" size={18} color="white" />
          </TouchableOpacity>
        </View>
      )}

      {/* Chat input */}
      {isTicketList ? (
        <View style={styles.chatInputContainer}>
          <View style={styles.inputWrapper}>
            <CCTextInput
              placeholder="Write a message"
              style={styles.textInput}
              value={message}
              onChangeText={setMessage}
            />
            <TouchableOpacity
              style={[
                styles.sendBtnInside,
                {opacity: message || file ? 1 : 0.5},
              ]}
              disabled={!message && !file}
              onPress={handleSend}>
              <MaterialIcons name="send" size={22} color={CCColors.White} />
            </TouchableOpacity>
          </View>

          {/* Attachment */}
          <TouchableOpacity
            style={styles.attachBtn}
            onPress={() => {
              const input = document.getElementById(
                'fileInputUpload',
              ) as HTMLInputElement;
              input?.click();
            }}>
            <MaterialIcons
              name="attach-file"
              size={24}
              color={CCColors?.TextColor?.Primary}
            />
          </TouchableOpacity>

          <input
            type="file"
            accept=".jpg,.jpeg,.png,.mp4,.avi,.mpeg"
            style={{display: 'none'}}
            id="fileInputUpload"
            onChange={handleFileChange}
          />
        </View>
      ) : (
        <View
          style={[
            {
              justifyContent: 'center',
              flexDirection: 'row',
              alignItems: 'center',
            },
          ]}>
          <View
            style={[
              {height: 50, flexDirection: 'row', alignItems: 'center', gap: 8},
            ]}>
            <MaterialCommunityIcons name="information-outline" size={16} />
            <CCText fontSize={FontSizes[16]}>
              You cannot send any messages to this channel.
            </CCText>
          </View>
          <View style={{marginLeft: 12}}>
            <CCButton
              onPress={() => {
                setOpenModal(true);
              }}
              contentContainerStyle={{
                paddingHorizontal: 12,
              }}>
              <CCText fontSize={FontSizes[12]} fontWeight={600}>
                {RAISE_A_TICKET}
              </CCText>
            </CCButton>
          </View>
          <SupportModal
            openModal={openModal}
            handleClose={() => setOpenModal(false)}
          />
        </View>
      )}
    </View>
  );
};

export default ChatView;
